// Общие коды ошибок, которые модули могут возвращать через TRPCError или Result.
import { z } from "zod";

/**
 * Типовые tRPC error codes, которые встречаются в наших модулях.
 * НЕ исчерпывающий список tRPC — только те, что мы целенаправленно бросаем
 * и UI ожидает обрабатывать.
 */
export const trpcErrorCodeSchema = z.enum([
	"BAD_REQUEST",
	"UNAUTHORIZED",
	"FORBIDDEN",
	"NOT_FOUND",
	"CONFLICT",
	"TOO_MANY_REQUESTS",
	"PAYMENT_REQUIRED",
	"PRECONDITION_FAILED",
	"INTERNAL_SERVER_ERROR",
	"UNPROCESSABLE_CONTENT",
]);
export type TrpcErrorCode = z.infer<typeof trpcErrorCodeSchema>;

/**
 * Бизнес-ошибки на уровне домена.
 * Используются как причина внутри `err()` (neverthrow) или поля `reason` в Result-output.
 */
export const businessErrorCodeSchema = z.enum([
	// auth
	"SSO_STATE_INVALID",
	"SSO_CODE_EXPIRED",
	"REFRESH_TOKEN_REUSED",
	"REFRESH_TOKEN_EXPIRED",
	"SESSION_REVOKED",
	"CONSENT_REQUIRED",
	"AGE_RESTRICTION",
	"DELETION_GRACE_EXPIRED",
	"REAUTH_REQUIRED",
	// users / onboarding
	"LOCALE_NOT_SUPPORTED",
	"INVALID_IANA_TIMEZONE",
	"ONBOARDING_REQUIRED_STEPS_MISSING",
	"AVATAR_TOO_LARGE",
	// billing / quotas
	"SUBSCRIPTION_NOT_FOUND",
	"SUBSCRIPTION_ACTIVE_ALREADY",
	"TRIAL_ALREADY_USED",
	"PROVIDER_UNAVAILABLE",
	"QUOTA_EXCEEDED",
	"STORAGE_QUOTA_EXCEEDED",
	"INVOICE_NOT_FOUND",
	"REFUND_NOT_ELIGIBLE",
	"DOWNGRADE_BLOCKED",
	// notifications / push
	"NOTIFICATION_NOT_FOUND",
	"PUSH_TOKEN_INVALID",
	// sync
	"SYNC_HANDLER_NOT_REGISTERED",
	"SYNC_BATCH_TOO_LARGE",
	"SYNC_JTI_REVOKED",
	// модульная платформа
	"MODULE_UNAVAILABLE",
]);
export type BusinessErrorCode = z.infer<typeof businessErrorCodeSchema>;

/**
 * Почему модуль недоступен (MODULE-PLATFORM-ARCHITECTURE §6.1).
 *
 * Одного кода `MODULE_UNAVAILABLE` фронту мало: `tier_locked` — это апселл,
 * `killed` — баннер аварии «временно», `platform_unsupported` — «нет на этой
 * платформе». Три разных экрана из одной ошибки.
 */
export const moduleUnavailableReasonSchema = z.enum([
	/** Админ выключил модуль в продукте. */
	"product_disabled",
	/** Ops-killswitch (авария на нашей стороне). */
	"killed",
	/** Тариф ниже `minTier` манифеста. */
	"tier_locked",
	/** Модуля нет на этой платформе (web / capacitor). */
	"platform_unsupported",
	/** Выключен модуль-зависимость (`requires`). */
	"dependency_missing",
	/** Внешний сервис модуля не отвечает (health красный). */
	"external_down",
	/** Модуль снят с эксплуатации. */
	"lifecycle_removed",
]);
export type ModuleUnavailableReason = z.infer<typeof moduleUnavailableReasonSchema>;

/**
 * Разделитель машинного кода в `TRPCError.message`.
 *
 * Причина такой доставки: `cause` у `TRPCError` до клиента штатно НЕ доезжает
 * (error-formatter пробрасывает только `zodIssues`), а расширять общий
 * формат ради одного кода — трогать шов, через который идут все модули.
 * В проекте уже есть рабочий приём того же класса — `QUOTA_EXCEEDED:${kind}`
 * (`modules/quotas/quota-procedure.ts`), который фронт разбирает по префиксу.
 * Тиражируем его, а не изобретаем второй механизм.
 *
 * Формат: `MODULE_UNAVAILABLE:<moduleId>:<reason>`.
 */
export const MODULE_UNAVAILABLE_PREFIX = "MODULE_UNAVAILABLE";

/** Собрать `message` для `TRPCError` (см. `MODULE_UNAVAILABLE_PREFIX`). */
export function formatModuleUnavailableMessage(
	moduleId: string,
	reason: ModuleUnavailableReason,
): string {
	return `${MODULE_UNAVAILABLE_PREFIX}:${moduleId}:${reason}`;
}

/** Разобрать `message` обратно. `null` — это не ошибка недоступности модуля. */
export function parseModuleUnavailableMessage(
	message: string | null | undefined,
): { moduleId: string; reason: ModuleUnavailableReason } | null {
	if (!message) return null;
	const parts = message.split(":");
	if (parts.length !== 3 || parts[0] !== MODULE_UNAVAILABLE_PREFIX) return null;
	const [, moduleId, rawReason] = parts;
	const reason = moduleUnavailableReasonSchema.safeParse(rawReason);
	if (!moduleId || !reason.success) return null;
	return { moduleId, reason: reason.data };
}

/**
 * Унифицированная обёртка для бизнес-ошибки в Result-стиле.
 * Используется для tRPC outputs, которые могут вернуть ожидаемый сбой
 * без throw (UI отображает понятное сообщение по `code`).
 */
export const businessErrorSchema = z.object({
	/** Машинный код ошибки для switch-case в UI. */
	code: businessErrorCodeSchema,
	/** Опциональное человекочитаемое сообщение (i18n key или fallback). */
	message: z.string().optional(),
	/** Опциональные дополнительные детали (например, текущий usage при QUOTA_EXCEEDED). */
	details: z.record(z.string(), z.unknown()).optional(),
	/**
	 * Причина недоступности модуля — заполняется при `code: "MODULE_UNAVAILABLE"`.
	 * Для Result-стиля (без throw); при throw причина едет в `message`
	 * (см. `formatModuleUnavailableMessage`).
	 */
	reason: moduleUnavailableReasonSchema.optional(),
});
export type BusinessError = z.infer<typeof businessErrorSchema>;

/**
 * Фабрика дискриминированного Result<T, BusinessError> для tRPC outputs.
 * Используется когда хотим, чтобы UI знал какие конкретные business-ошибки могут прийти.
 */
export function resultSchema<TOk extends z.ZodTypeAny>(okSchema: TOk) {
	return z.discriminatedUnion("status", [
		z.object({ status: z.literal("ok"), data: okSchema }),
		z.object({ status: z.literal("error"), error: businessErrorSchema }),
	]);
}
